import { AnimatePresence, motion } from 'framer-motion';
import { type Translate } from 'next-translate';
import React, { type Dispatch, type SetStateAction, useRef } from 'react';
import { LinkWithQuery } from '@/components/elements/LinkWithQuery';
import { MyImage } from '@/components/elements/MyImage';
import { AmplitudeService } from '@/services';
import { type LandingItem, type LandingTool } from '@/data/aiToolLandings';
import { AMPLITUDE_EVENTS } from '@/constants';
import { positioningVariants } from '@/animations';
import { ScrollToTopButton } from '../ScrollToTopButton';
import styles from './ToolsGrid.module.scss';

interface Props {
	isTabsSticky: boolean;
	activeTabId: string;
	hoveredTabId: string | null;
	activeFeature: LandingTool | null;
	setActiveFeature: Dispatch<SetStateAction<LandingTool | null>>;
	featuresData: LandingTool[];
	positioningData: LandingItem[];
	setPinnedFeatureId: React.Dispatch<React.SetStateAction<string | null>>;
	scrollToolsIntoViewIfNeeded: () => void;
	t: Translate;
}

export const ToolsGrid = ({
	isTabsSticky,
	activeTabId,
	hoveredTabId,
	activeFeature,
	setActiveFeature,
	featuresData,
	positioningData,
	scrollToolsIntoViewIfNeeded,
	setPinnedFeatureId,
	t,
}: Props) => {
	const toolsListingRef = useRef<HTMLDivElement>(null);
	const positioningItemRef = useRef<HTMLDivElement>(null);

	const handleFeatureItemClick = (item: LandingTool) => {
		setActiveFeature(item);
		setPinnedFeatureId(null);
		scrollToolsIntoViewIfNeeded();
		AmplitudeService.logEvent(AMPLITUDE_EVENTS.ALL_TOOLS_FEATURE_SELECTED, {
			type: activeTabId,
			feature: t(item.title),
		});
	};

	const handlePositioningItemClick = (positioningItem: LandingItem) => {
		if (!activeFeature) return;
		AmplitudeService.logEvent(AMPLITUDE_EVENTS.ALL_TOOLS_SUBFEATURE_SELECTED, {
			type: activeTabId,
			feature: t(activeFeature.title),
			subfeature: positioningItem.titleEng,
		});
	};

	return (
		<>
			<div ref={toolsListingRef} className={styles['tools-listing']}>
				<AnimatePresence mode='wait'>
					<motion.div
						key={hoveredTabId || activeTabId}
						initial={{ opacity: 0, y: 8 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.2, ease: 'easeOut' }}
						className={styles['features']}
					>
						<>
							<div
								className={styles['gradient-overlay']}
								style={{ opacity: isTabsSticky ? 1 : 0 }}
							/>
							<div className={styles['features__scroll']}>
								{featuresData.map(item => (
									<button
										key={item.title}
										type='button'
										onClick={() => handleFeatureItemClick(item)}
										className={`${styles['features__item']} ${
											activeFeature && item.title === activeFeature.title
												? styles['features__item_active']
												: ''
										}`}
									>
										{t(item.title)}
									</button>
								))}
							</div>
						</>
					</motion.div>
				</AnimatePresence>

				<AnimatePresence mode='wait'>
					<motion.div
						key={hoveredTabId || activeTabId}
						variants={positioningVariants}
						initial='initial'
						animate='animate'
						exit='exit'
						transition={{ duration: 0.25, ease: 'easeOut' }}
						className={styles['positioning']}
					>
						{positioningData.map(item => (
							<div
								key={item.id}
								ref={positioningItemRef}
								role='button'
								tabIndex={0}
								onClick={() => handlePositioningItemClick(item)}
								onKeyDown={() => handlePositioningItemClick(item)}
							>
								<LinkWithQuery
									href={item.href ?? ''}
									aria-label={t(item.title)}
									excludeParams={['category']}
									className={styles['positioning__card']}
								>
									<MyImage
										imageProps={{
											src: item.image,
											alt: '',
											title: item.titleEng,
										}}
										settings={{
											classes: {
												picture: styles['positioning__card_picture'],
												image: styles['positioning__card_image'],
											},
										}}
									/>
								</LinkWithQuery>

								<h2 className={styles['positioning__card_title']}>
									{item.titleEng}
								</h2>
							</div>
						))}
					</motion.div>
				</AnimatePresence>
			</div>

			<ScrollToTopButton
				scrollableContainer='window'
				itemRef={positioningItemRef}
				className={styles['positioning__scroll-btn']}
				customScrollWindowPositon={toolsListingRef.current?.offsetTop}
			/>
		</>
	);
};
